import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getClaim, updateClaim, transitionClaim, deleteClaim, downloadCMS1500, downloadEDI837, downloadSuperbill, submitClaim } from '../services/insuranceService';
import { checkClaimStatus } from '../services/eligibilityService';
import { getPayerRates } from '../services/feeScheduleService';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import Modal from '../components/common/Modal';

const STATUS_VARIANTS = {
  draft: 'default',
  submitted: 'info',
  pending: 'warning',
  paid: 'success',
  denied: 'error',
  partial: 'warning',
};

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

const paymentSchema = z.object({
  allowedAmount: z.coerce.number().min(0).optional(),
  paidAmount: z.coerce.number().min(0, 'Required'),
  adjustmentAmount: z.coerce.number().min(0).optional(),
  denialReason: z.string().optional().or(z.literal('')),
});

function RecordPaymentForm({ claim, onSubmit, onCancel, isSubmitting }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      allowedAmount: claim.allowedAmount || '',
      paidAmount: claim.billedAmount,
      adjustmentAmount: claim.adjustmentAmount || 0,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Allowed amount</label>
          <input type="number" step="0.01" min="0" className="input" {...register('allowedAmount')} />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Paid amount *</label>
          <input type="number" step="0.01" min="0" className="input" {...register('paidAmount')} />
          {errors.paidAmount && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.paidAmount.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Adjustment</label>
          <input type="number" step="0.01" min="0" className="input" {...register('adjustmentAmount')} />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn btn-primary disabled:opacity-60">
          {isSubmitting ? 'Saving…' : 'Record payment'}
        </button>
      </div>
    </form>
  );
}

export default function InsuranceClaimDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingEDI, setIsDownloadingEDI] = useState(false);
  const [isDownloadingSuperbill, setIsDownloadingSuperbill] = useState(false);
  const [superbillMissing, setSuperbillMissing] = useState(null);
  const [statusCheck, setStatusCheck] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['claim', id],
    queryFn: () => getClaim(id),
  });

  const { data: ratesData } = useQuery({
    queryKey: ['payerRates'],
    queryFn: () => getPayerRates(),
  });
  const allPayerRates = ratesData?.rates ?? [];

  const transitionMutation = useMutation({
    mutationFn: (status) => transitionClaim(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', id] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      toast.success('Status updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update status'),
  });

  const paymentMutation = useMutation({
    mutationFn: async (formData) => {
      await updateClaim(id, {
        allowedAmount: formData.allowedAmount || null,
        paidAmount: formData.paidAmount,
        adjustmentAmount: formData.adjustmentAmount || 0,
      });
      const paid = parseFloat(formData.paidAmount);
      const billed = parseFloat(data.claim.billedAmount);
      const newStatus = paid >= billed ? 'paid' : 'partial';
      return transitionClaim(id, newStatus);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claim', id] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      setIsPaymentOpen(false);
      toast.success('Payment recorded');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to record payment'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteClaim(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      navigate('/insurance');
      toast.success('Claim deleted');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete claim'),
  });

  const submitMutation = useMutation({
    mutationFn: () => submitClaim(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['claim', id] });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
      setSubmitResult(result.data);
      toast.success('Claim submitted to clearinghouse');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to submit claim'),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600">Failed to load claim.</div>;

  const { claim } = data;
  const isDraft = claim.status === 'draft';
  const isSubmitted = claim.status === 'submitted';
  const isPending = claim.status === 'pending';
  const canPayOrDeny = ['submitted', 'pending'].includes(claim.status);
  const canDelete = isDraft;

  // Reconciliation: match payer contracted rates to service lines
  const findContractedRate = (cptCode, serviceDate) => {
    const asOf = serviceDate || new Date().toISOString().slice(0, 10);
    return allPayerRates.find(
      (r) =>
        r.payerName.toLowerCase() === claim.payerName?.toLowerCase() &&
        r.cptCode.toUpperCase() === cptCode?.toUpperCase() &&
        r.effectiveDate <= asOf &&
        (!r.endDate || r.endDate >= asOf),
    );
  };
  const recoLines = (claim.serviceLines || []).map((line) => {
    const rate = findContractedRate(line.cptCode, line.serviceDate);
    const contracted = rate ? parseFloat(rate.contractedRate) : null;
    const expected = contracted != null ? contracted * (line.units || 1) : null;
    return { ...line, contracted, expected };
  });
  const totalExpected = recoLines.reduce((s, l) => s + (l.expected ?? 0), 0);
  const hasContractedRates = recoLines.some((l) => l.contracted != null);
  const isPaidOrPartial = ['paid', 'partial'].includes(claim.status);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/insurance" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{claim.payerName}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge label={claim.status} variant={STATUS_VARIANTS[claim.status] || 'default'} />
              {claim.client && (
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  · {claim.client.firstName} {claim.client.lastName}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap justify-end">
          {isDraft && (
            <>
              <button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
                className="btn btn-primary text-sm disabled:opacity-60"
                title="Electronically submit to payer via clearinghouse (837P)"
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit to Payer'}
              </button>
              <button
                onClick={() => transitionMutation.mutate('submitted')}
                disabled={transitionMutation.isPending}
                className="btn btn-secondary text-sm"
                title="Mark as submitted without electronic submission"
              >
                Mark submitted
              </button>
            </>
          )}
          {isSubmitted && (
            <button
              onClick={() => transitionMutation.mutate('pending')}
              disabled={transitionMutation.isPending}
              className="btn btn-secondary text-sm"
            >
              Mark pending
            </button>
          )}
          {canPayOrDeny && (
            <>
              <button onClick={() => setIsPaymentOpen(true)} className="btn btn-primary text-sm">
                Record payment
              </button>
              <button
                onClick={() => transitionMutation.mutate('denied')}
                disabled={transitionMutation.isPending}
                className="btn btn-secondary text-sm text-danger-600"
              >
                Mark denied
              </button>
            </>
          )}
          {(isDraft || isSubmitted || isPending) && (
            <Link to={`/insurance/${id}/edit`} className="btn btn-secondary text-sm">Edit</Link>
          )}
          {!isDraft && (
            <button
              onClick={async () => {
                setIsCheckingStatus(true);
                try {
                  const result = await checkClaimStatus(id);
                  setStatusCheck(result);
                  toast.success('Status check complete');
                } catch {
                  toast.error('Status check failed');
                }
                finally { setIsCheckingStatus(false); }
              }}
              disabled={isCheckingStatus}
              className="btn btn-secondary text-sm disabled:opacity-60"
            >
              {isCheckingStatus ? 'Checking…' : 'Check status'}
            </button>
          )}
          <button
            onClick={async () => {
              setIsDownloadingSuperbill(true);
              setSuperbillMissing(null);
              try {
                await downloadSuperbill(id, claim.claimNumber);
                toast.success('Superbill downloaded');
              } catch (err) {
                if (err.missing?.length > 0) setSuperbillMissing(err.missing);
                else toast.error('Failed to generate superbill');
              } finally { setIsDownloadingSuperbill(false); }
            }}
            disabled={isDownloadingSuperbill}
            className="btn btn-primary text-sm disabled:opacity-60"
          >
            {isDownloadingSuperbill ? 'Generating…' : 'Download Superbill'}
          </button>
          <button
            onClick={async () => {
              setIsDownloading(true);
              try { await downloadCMS1500(id); } catch { toast.error('Failed to download CMS-1500'); }
              finally { setIsDownloading(false); }
            }}
            disabled={isDownloading}
            className="btn btn-secondary text-sm disabled:opacity-60"
          >
            {isDownloading ? 'Generating…' : 'Download CMS-1500'}
          </button>
          <button
            onClick={async () => {
              setIsDownloadingEDI(true);
              try { await downloadEDI837(id, claim.claimNumber); } catch { toast.error('Failed to generate EDI 837P'); }
              finally { setIsDownloadingEDI(false); }
            }}
            disabled={isDownloadingEDI}
            className="btn btn-secondary text-sm disabled:opacity-60"
          >
            {isDownloadingEDI ? 'Generating…' : 'Download EDI 837P'}
          </button>
          {canDelete && (
            <button onClick={() => setIsDeleteOpen(true)} className="btn btn-secondary text-sm text-danger-600">
              Delete
            </button>
          )}
        </div>
      </div>

      {superbillMissing && (
        <div className="card mb-6 border-l-4 border-amber-400 dark:border-amber-500">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Superbill requires additional information
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                Complete the following fields before generating the superbill:
              </p>
            </div>
            <button onClick={() => setSuperbillMissing(null)} className="text-neutral-400 hover:text-neutral-600 text-xs ml-4">
              Dismiss
            </button>
          </div>
          <ul className="space-y-2">
            {superbillMissing.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 w-2 h-2 rounded-full bg-amber-400 dark:bg-amber-500 shrink-0" />
                <span className="text-neutral-700 dark:text-neutral-300">
                  <span className="font-medium">{item.field}</span>
                  {' — '}
                  <Link
                    to={item.path}
                    className="text-primary-600 hover:text-primary-700 underline"
                    onClick={() => setSuperbillMissing(null)}
                  >
                    {item.location}
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card mb-6">
        <div className="grid grid-cols-3 gap-6 text-sm">
          {[
            ['Payer ID', claim.payerId],
            ['Subscriber ID', claim.subscriberId],
            ['Claim #', claim.claimNumber],
            ['Place of service', claim.placeOfService],
            ['Rendering NPI', claim.renderingProviderNpi],
            ['Submitted', claim.submittedDate ? format(parseISO(claim.submittedDate), 'MMM d, yyyy') : null],
            ['Clearinghouse ID', claim.clearinghouseTrackingId],
            ['Billed', fmt(claim.billedAmount)],
            ['Allowed', claim.allowedAmount ? fmt(claim.allowedAmount) : null],
            ['Paid', fmt(claim.paidAmount)],
            ['Adjustment', fmt(claim.adjustmentAmount)],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label}>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">{label}</p>
              <p className="text-neutral-900 dark:text-neutral-100">{value}</p>
            </div>
          ))}
          {claim.diagnosisCodes?.filter(Boolean).length > 0 && (
            <div className="col-span-3">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">Diagnosis codes (ICD-10)</p>
              <div className="flex flex-wrap gap-2">
                {claim.diagnosisCodes.filter(Boolean).map((code, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 text-xs font-mono">
                    <span className="font-bold text-primary-600">{'ABCDEFGHIJKL'[i]}</span> {code}
                  </span>
                ))}
              </div>
            </div>
          )}
          {claim.denialReason && (
            <div className="col-span-3">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Denial reason</p>
              <p className="text-danger-600 dark:text-danger-400">{claim.denialReason}</p>
            </div>
          )}
          {claim.notes && (
            <div className="col-span-3">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-neutral-700 dark:text-neutral-300">{claim.notes}</p>
            </div>
          )}
        </div>
      </div>

      {submitResult && (
        <div className="card mb-6 border-l-4 border-green-400 dark:border-green-500">
          <div className="flex items-start justify-between mb-2">
            <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Claim Submitted to Clearinghouse</h2>
            <button onClick={() => setSubmitResult(null)} className="text-neutral-400 hover:text-neutral-600 text-xs">Dismiss</button>
          </div>
          <div className="text-sm space-y-1">
            {submitResult.trackingId && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Tracking ID:</span> <span className="font-mono">{submitResult.trackingId}</span></p>
            )}
            {submitResult.submittedDate && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Submitted:</span> {submitResult.submittedDate}</p>
            )}
            <p className="text-xs text-neutral-400 dark:text-neutral-500 pt-1">
              Claim status will update as the payer processes it. Use &ldquo;Check status&rdquo; to poll for updates.
            </p>
          </div>
        </div>
      )}

      {statusCheck && (
        <div className="card mb-6 border-l-4 border-blue-400 dark:border-blue-500">
          <div className="flex items-start justify-between mb-2">
            <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Clearinghouse Status Check</h2>
            <button onClick={() => setStatusCheck(null)} className="text-neutral-400 hover:text-neutral-600 text-xs">Dismiss</button>
          </div>
          <div className="text-sm space-y-1">
            {statusCheck.statusCode && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Code:</span> <span className="font-mono">{statusCheck.statusCode}</span></p>
            )}
            {statusCheck.statusDescription && (
              <p className="text-neutral-700 dark:text-neutral-300">{statusCheck.statusDescription}</p>
            )}
            {statusCheck.adjudicationDate && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Adjudication date:</span> {statusCheck.adjudicationDate}</p>
            )}
            {statusCheck.paidAmount != null && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Paid amount:</span> {fmt(statusCheck.paidAmount)}</p>
            )}
            {statusCheck.checkNumber && (
              <p><span className="font-medium text-neutral-600 dark:text-neutral-400">Check #:</span> {statusCheck.checkNumber}</p>
            )}
            <p className="text-xs text-neutral-400 dark:text-neutral-500 pt-1">Checked at {new Date(statusCheck.checkedAt || Date.now()).toLocaleTimeString()}</p>
          </div>
        </div>
      )}

      {claim.serviceLines?.length > 0 && (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Service Lines</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-3">Date</th>
                <th className="pb-2 pr-3">CPT</th>
                <th className="pb-2 pr-3">Modifiers</th>
                <th className="pb-2 pr-3">Dx</th>
                <th className="pb-2 pr-3 text-right">Units</th>
                <th className="pb-2 text-right">Charge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {claim.serviceLines.map((line) => (
                <tr key={line.id}>
                  <td className="py-2 pr-3 text-neutral-600 dark:text-neutral-400">
                    {line.serviceDate ? format(parseISO(line.serviceDate), 'MM/dd/yy') : '—'}
                  </td>
                  <td className="py-2 pr-3 font-mono font-medium text-neutral-900 dark:text-neutral-100">{line.cptCode}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-neutral-500">
                    {line.modifiers?.length > 0 ? line.modifiers.join(', ') : '—'}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-neutral-500">
                    {line.diagnosisPointers?.length > 0 ? line.diagnosisPointers.join(', ') : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right text-neutral-700 dark:text-neutral-300">{line.units}</td>
                  <td className="py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">{fmt(line.chargeAmount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reconciliation panel */}
      {hasContractedRates && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
              Payment Reconciliation
            </h2>
            {isPaidOrPartial && (() => {
              const variance = parseFloat(claim.paidAmount || 0) - totalExpected;
              const isShort = variance < -0.01;
              const isOver = variance > 0.01;
              return (
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  isShort ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-400' :
                  isOver ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                }`}>
                  {isShort ? `Underpaid ${fmt(Math.abs(variance))}` : isOver ? `Overpaid ${fmt(variance)}` : 'On contract'}
                </span>
              );
            })()}
          </div>
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-700">
                <th className="pb-2 pr-3">CPT</th>
                <th className="pb-2 pr-3 text-right">Units</th>
                <th className="pb-2 pr-3 text-right">Contracted/unit</th>
                <th className="pb-2 text-right">Expected total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700/50">
              {recoLines.map((line) => (
                <tr key={line.id}>
                  <td className="py-2 pr-3 font-mono font-medium text-neutral-900 dark:text-neutral-100">{line.cptCode}</td>
                  <td className="py-2 pr-3 text-right text-neutral-700 dark:text-neutral-300">{line.units}</td>
                  <td className="py-2 pr-3 text-right text-neutral-700 dark:text-neutral-300">
                    {line.contracted != null ? fmt(line.contracted) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-2 text-right font-medium text-neutral-900 dark:text-neutral-100">
                    {line.expected != null ? fmt(line.expected) : <span className="text-neutral-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            {[
              ['Expected (contracted)', fmt(totalExpected)],
              ['Billed', fmt(claim.billedAmount)],
              ...(isPaidOrPartial ? [
                ['Paid', fmt(claim.paidAmount)],
                ['Variance', (() => {
                  const v = parseFloat(claim.paidAmount || 0) - totalExpected;
                  const color = v < -0.01 ? 'text-danger-600 dark:text-danger-400' : v > 0.01 ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400';
                  return <span className={`font-semibold ${color}`}>{v >= 0 ? '+' : ''}{fmt(v)}</span>;
                })()],
              ] : []),
            ].map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-0.5">{label}</p>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(claim.invoice || claim.appointment || claim.era) && (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Linked records</h2>
          <div className="space-y-2 text-sm">
            {claim.invoice && (
              <div className="flex items-center gap-2">
                <span className="text-neutral-500 dark:text-neutral-400">Invoice:</span>
                <Link to={`/billing/${claim.invoice.id}`} className="text-primary-600 hover:underline">
                  {claim.invoice.invoiceNumber}
                </Link>
              </div>
            )}
            {claim.appointment && (
              <div className="flex items-center gap-2">
                <span className="text-neutral-500 dark:text-neutral-400">Appointment:</span>
                <span className="text-neutral-900 dark:text-neutral-100">
                  {format(new Date(claim.appointment.startTime), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
            )}
            {claim.era && (
              <div className="flex items-center gap-2">
                <span className="text-neutral-500 dark:text-neutral-400">ERA:</span>
                <Link to={`/insurance/eras/${claim.era.id}`} className="text-primary-600 hover:underline">
                  {claim.era.payerName} — {claim.era.checkNumber || format(parseISO(claim.era.paymentDate), 'MMM d, yyyy')}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal isOpen={isPaymentOpen} onClose={() => setIsPaymentOpen(false)} title="Record Payment" size="md">
        <RecordPaymentForm
          claim={claim}
          onSubmit={(formData) => paymentMutation.mutate(formData)}
          onCancel={() => setIsPaymentOpen(false)}
          isSubmitting={paymentMutation.isPending}
        />
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} title="Delete claim?" size="sm">
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">
          This will permanently delete this draft claim. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setIsDeleteOpen(false)} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="btn btn-danger disabled:opacity-60"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
